'use client';

import { usePathname, useRouter } from 'next/navigation';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { path } from '@/i18n/routing';
import { useLocale } from '@/i18n/useLocale';
import { cn } from '@/lib/cn';

/**
 * The cinematic route from any "Loyihaga buyurtma berish" CTA to the ariza
 * form: a navy curtain rises over the current page, the route changes
 * underneath it, the viewport is pinned on the form, and the curtain lifts
 * to reveal it mid-entrance.
 *
 * The provider owns the overlay and exposes a single `startOrder()` through
 * context; CTAs stay real links so modified clicks and no-JS keep native
 * behaviour.
 */

/** Session flag the aloqa form reads to play its arrival choreography. */
const ARRIVAL_FLAG = 'mpmg-ariza-arrival';

/** Fired instead of a navigation when the visitor is already on /aloqa. */
export const ARRIVAL_EVENT = 'mpmg:ariza-arrival';

/* Cover = panels up + wordmark in; hold = new page settles under the
 * curtain; leave = panels lift. Values track the CSS timings below. */
const COVER_MS = 780;
const HOLD_MS = 240;
const LEAVE_MS = 820;

type Phase = 'idle' | 'cover' | 'leave';

const OrderTransitionContext = createContext<(() => void) | null>(null);

/** Null when no provider is mounted — callers fall back to plain links. */
export function useOrderTransition() {
  return useContext(OrderTransitionContext);
}

/** One-shot read of the arrival flag set just before the route change. */
export function consumeArrivalFlag(): boolean {
  try {
    if (sessionStorage.getItem(ARRIVAL_FLAG)) {
      sessionStorage.removeItem(ARRIVAL_FLAG);
      return true;
    }
  } catch {
    /* Storage may be unavailable (private mode) — arrive without ceremony. */
  }
  return false;
}

function setArrivalFlag() {
  try {
    sessionStorage.setItem(ARRIVAL_FLAG, '1');
  } catch {
    /* Same trade-off as above. */
  }
}

export function OrderTransitionProvider({
  children,
  /** The curtain's own copy, in the language of the page underneath it. */
  copy,
}: {
  children: ReactNode;
  copy: { title: string; lead: string };
}) {
  const router = useRouter();
  const pathname = usePathname();
  const lang = useLocale();
  /** The ariza page in the language currently being read. */
  const contactPath = path(lang, 'contact');
  const [phase, setPhase] = useState<Phase>('idle');
  const awaitingArrival = useRef(false);

  const startOrder = useCallback(() => {
    if (awaitingArrival.current) return;

    const reduceMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;

    setArrivalFlag();

    if (pathname === contactPath) {
      // Already on the page: no route trip — scroll to the form and let it
      // replay its spotlight. The rAF lets a just-closed mobile menu release
      // its body scroll lock first.
      window.requestAnimationFrame(() => {
        document.getElementById('ariza')?.scrollIntoView({
          // 'auto' respects the html scroll-behavior, which the global
          // reduced-motion override already forces to instant.
          behavior: reduceMotion ? 'auto' : 'smooth',
          block: 'start',
        });
        window.dispatchEvent(new Event(ARRIVAL_EVENT));
      });
      return;
    }

    if (reduceMotion) {
      router.push(`${contactPath}#ariza`);
      return;
    }

    setPhase('cover');
    awaitingArrival.current = true;
    window.setTimeout(() => router.push(contactPath), COVER_MS);
  }, [contactPath, pathname, router]);

  // The route arrived while covered: pin the viewport on the form before
  // anything is visible, give the page a beat to paint, then lift.
  useEffect(() => {
    if (!awaitingArrival.current || pathname !== contactPath) return;
    awaitingArrival.current = false;

    document
      .getElementById('ariza')
      ?.scrollIntoView({ behavior: 'instant', block: 'start' });

    const lift = window.setTimeout(() => setPhase('leave'), HOLD_MS);
    const settle = window.setTimeout(
      () => setPhase('idle'),
      HOLD_MS + LEAVE_MS,
    );
    return () => {
      window.clearTimeout(lift);
      window.clearTimeout(settle);
    };
  }, [contactPath, pathname]);

  // Safety net: a stalled navigation must never leave the page covered.
  useEffect(() => {
    if (phase !== 'cover') return;
    const bail = window.setTimeout(() => {
      if (awaitingArrival.current) {
        awaitingArrival.current = false;
        setPhase('idle');
      }
    }, 4000);
    return () => window.clearTimeout(bail);
  }, [phase]);

  return (
    <OrderTransitionContext.Provider value={startOrder}>
      {children}

      <div
        aria-hidden="true"
        className={cn(
          'order-curtain',
          phase === 'cover' && 'is-cover',
          phase === 'leave' && 'is-leave',
        )}
      >
        <div className="order-curtain__panel" />
        <div className="order-curtain__panel" />
        <div className="order-curtain__panel" />

        <div className="order-curtain__veil">
          <div aria-hidden="true" className="absolute inset-0 grid-bg" />
          <div
            aria-hidden="true"
            className="absolute top-1/2 left-1/2 size-[30rem] -translate-1/2 rounded-full bg-brand-600/20 blur-[110px]"
          />
          <div className="relative flex flex-col items-center gap-5 px-6 text-center">
            <span className="label-xs text-ondark-faint">MPMG SOFT</span>
            <span className="order-curtain__line">
              <span className="order-curtain__line-bar" />
            </span>
            <p className="font-display text-[1.0625rem] font-semibold text-white">
              {copy.title}
            </p>
            <p className="text-[0.8125rem] text-ondark-muted">{copy.lead}</p>
          </div>
        </div>
      </div>
    </OrderTransitionContext.Provider>
  );
}
